import {
    Alert,
    Stack,
    Modal,
    Button,
    Typography,
    Box,
    CardContent,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { useList, useTranslate, useModal, useCustom, useSelect, useNotification, useGo } from "@refinedev/core";
import { CreateOrEdit } from "@/components/editcreate";
import { TextFieldElement, } from "react-hook-form-mui";
import { RHFAutoCompleteString } from "@/components/form/RHFAutocompleteString";
import { zodResolver } from "@hookform/resolvers/zod";
import { accountCreateEditSchema, IContactFormValuesType } from "@/utils/zod/contactFormValidation";
import { RestDataProvider } from "@/utils/data-provider/provider";
import { CompanyFormSchema } from "@/utils/zod/companyFormValidation";
import { API_URL as apiUrl } from "@/utils";
import { CompanyAddEditForm } from "./CompanyAddEditForm";
import { RHFAutocomplete } from "./RHFAutocomplete";
import { useParams, useLocation } from "react-router";
import PhoneNumberField from "../override/PhoneNumberField";
import { useEffect, useRef } from "react";
import { Card } from "../card";

interface Props {
    entityType: "account" | "team-member";
    actionType: "create" | "edit";
}

export const AccountAddEditForm = ({ entityType, actionType }: Props) => {

    const t = useTranslate();
    const { id } = useParams();
    const go = useGo();
    const { search } = useLocation();

    const { open } = useNotification();

    const { data: companies } = useList({
        resource: "companies",
        queryOptions: {
            enabled: entityType === "account",
        }
    });

    const { options: rolePermissions } = useSelect({
        resource: "role_permissions",
        optionLabel: (record) => record.name.charAt(0).toUpperCase() + record.name.slice(1),
        optionValue: "name",
    });

    const { visible: confirmCompanyVisible, show: confirmCompanyShow, close: confirmCompanyClose } = useModal();
    const { visible: newCompanyVisible, show: newCompanyShow, close: newCompanyClose } = useModal();
    const { visible: newCompanyCreateVisible, show: newCompanyCreateShow, close: newCompanyCreateClose } = useModal();

    const hasCheckedCompany = useRef(false);

    const formValues = useForm<IContactFormValuesType<typeof actionType, typeof entityType>>({
        mode: "onChange",
        refineCoreProps: {
            resource: "accounts",
            action: actionType,
            id: id,
            redirect: false,
            successNotification: () => {
                return false;
            },
            onMutationSuccess: (data: any) => {
                if (actionType === "create") {
                    data.data.forEach((contact: any) => {
                        if (contact.error) {
                            open?.({
                                message: contact.error,
                                type: "error",
                            })
                        } else {
                            open?.({
                                message: `Contact ${contact.user.phone_number} created successfully`,
                                type: "success",
                            });
                        }
                    })
                    go({
                        to: entityType === "account" ? "/accounts" : "/accounts/team-members",
                        type: "push",
                        query: Object.fromEntries(new URLSearchParams(search))
                    });
                } else {
                    if (data.data.error) {
                        open?.({
                            message: data.data.error,
                            type: "error",
                        });
                    } else {
                        open?.({
                            message: `Contact ${data.data.user.phone_number} updated successfully`,
                            type: "success",
                        });
                    }
                    go({
                        to: entityType === "account" ? "/accounts" : "/accounts/team-members",
                        type: "push",
                        query: Object.fromEntries(new URLSearchParams(search))
                    });
                }
            },
            queryOptions: {
                select: (data) => {
                    // Remove 91 prefix when loading the data
                    if (data?.data?.user?.phone_number && data.data.user.phone_number.toString().startsWith('91') && data.data.user.phone_number.toString().length === 12) {
                        data.data.user.phone_number = Number(data.data.user.phone_number.toString().substring(2));
                    }
                    data.data.notes = data.data.user?.description ?? data.data.notes ?? '';
                    return data;
                }
            }
        },
        resolver: zodResolver(accountCreateEditSchema(entityType, actionType)),
        ...(actionType === "create" && entityType === "account" && {
            defaultValues: {
                name: "",
                role_permission_name: "",
                tags: [],
                status: "approved",
                notes: "",
                company_ledger_name: "",
                designation: "",
                user: {
                    phone_number: "",
                },
            },
        }),
        ...(actionType === "create" && entityType === "team-member" && {
            defaultValues: {
                name: "",
                user: {
                    phone_number: "",
                },
                status: "approved",
                role_permission_name: "",
                tags: [],
                notes: "",
            },
        }),
    });

    const { handleSubmit, refineCore: { onFinish }, formState: { errors, dirtyFields } } = formValues;
    if (actionType === "edit") {
        formValues.register("id");
        !formValues.getValues("name") && formValues.setValue("name", formValues.getValues("user.name"));
    }

    const onSubmit = handleSubmit(async (values) => {
        // Check if phone number field was modified
        const isPhoneNumberChanged = dirtyFields.user?.phone_number;

        // Convert phone number to number type
        const phoneNumber = values.user?.phone_number ? Number(values.user.phone_number) : undefined;

        const valuesToSubmit = {
            ...values,
            user: isPhoneNumberChanged ? {
                phone_number: phoneNumber ? Number(`91${phoneNumber}`) : undefined,
                account_id: values.id,
                avatar_file_name: null,
                description: null,
                name: null,
            } : {
                phone_number: phoneNumber ? Number(`91${phoneNumber}`) : undefined
            },
            role_permission_name: values.role_permission_name?.toLowerCase() || "user",
            company_ledger_name: values.company_ledger_name?.trim().toUpperCase(),
            designation: values.designation?.toLowerCase(),
        };
        onFinish(actionType === "create" ? [valuesToSubmit] : valuesToSubmit);
    });

    const handleConfirmExistingCompany = () => {
        formValues.setValue("company_ledger_name", formValues.getValues("existing-companies")?.[0]?.ledger_name);
        hasCheckedCompany.current = true;
        confirmCompanyClose();
    };

    const handleNewCompanySave = (ledgerName: string) => {
        formValues.setValue("company_ledger_name", ledgerName);
        newCompanyClose();
    };

    const { data: designationOptions } = useCustom<string[]>({
        url: "/accounts/account-designations",
        method: "get",
    });

    const { data: tags } = useCustom<string[]>({
        url: "/contact-tags/static",
        method: "get",
    });

    useEffect(() => {
        const companyLedgerName = formValues.getValues("company_ledger_name");
        const companyDetails = formValues.getValues("user.company_details_form");

        const gstin = companyDetails?.gstin;
        const panNumber = companyDetails?.pan_number;

        // Exit early if ledger name exists OR company name is missing
        if (companyLedgerName || !companyDetails?.name) return;

        // Exit if both gstin and pan are empty (avoid useless API call)
        if (!gstin && !panNumber) return;

        // Now make the call
        RestDataProvider(apiUrl).getList<CompanyFormSchema>({
            resource: `companies/existing-companies?gstin=${gstin || ""}&pan=${panNumber || ""}`,
        }).then(({ data }) => {
            formValues.setValue("existing-companies", data);
            if (data.length > 0) {
                confirmCompanyShow();
            } else {
                newCompanyShow();
            }
        });
    }, [
        formValues.watch("company_ledger_name"),
    ]);

    return (
        <>
            <CreateOrEdit actionType={actionType} formValues={formValues} handleSubmit={onSubmit} title={{ edit: `Edit ${entityType === "account" ? "Account" : "Team Member"} #${formValues.getValues("user.phone_number")}` }}>
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }} autoComplete="off">
                    <Stack direction="column" spacing={2} sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2}>
                            <TextFieldElement
                                name="name"
                                label={t("accounts.fields.name")}
                                autoComplete="no-name"
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <PhoneNumberField
                                name="user.phone_number"
                                label={t("accounts.fields.phone_number")}
                                onChangeCallback={(e) => {
                                    const value = e.target.value;
                                    // Only allow numbers
                                    if (value === '' || /^[0-9]+$/.test(value)) {
                                        formValues.setValue('user.phone_number', value ? Number(value) : undefined);
                                    }
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                                autoFocus={true}
                            />
                            {entityType === "account" && (
                                <RHFAutocomplete
                                    name="role_permission_name"
                                    label={t("accounts.fields.role_permission_name")}
                                    options={rolePermissions.filter((item) => item.value !== "super_admin" && item.value !== "ai_bot")}
                                    multiple={false}
                                    createNew={false}
                                    required
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                            )}
                        </Stack>

                        <Stack direction="row" spacing={2}>
                            {entityType === "account" && (
                                <RHFAutoCompleteString
                                    name="company_ledger_name"
                                    label={t("accounts.fields.company")}
                                    options={companies?.data.map(company => company.ledger_name) ?? []}
                                    multiple={false}
                                    sx={{ flex: 1 }}
                                    createNew={true}
                                    onCreateNew={() => {
                                        newCompanyCreateShow();
                                    }}
                                    size="small"
                                />
                            )}

                            <RHFAutoCompleteString
                                name="status"
                                label={t("accounts.fields.status.label")}
                                options={["approved", "banned", "pending"]}
                                sx={{ flex: 1 }}
                                size="small"
                            />

                            {entityType === "team-member" &&
                                (<RHFAutocomplete
                                    name="role_permission_name"
                                    label={t("accounts.fields.role_permission_name")}
                                    options={rolePermissions.filter((item) => item.value !== "user" && item.value !== "super_admin" && item.value !== "ai_bot")}
                                    sx={{ flex: 1 }}
                                    multiple={false}
                                    createNew={false}
                                    required
                                    size="small"
                                />)
                            }

                        </Stack>
                        {entityType === "account" && (
                            <Stack direction="row" spacing={2}>
                                <RHFAutoCompleteString
                                    name="designation"
                                    createNew
                                    label={t("accounts.fields.designation")}
                                    options={designationOptions?.data ?? []}
                                    multiple={false}
                                    sx={{ flex: 1 }}
                                    fullWidth
                                    size="small"
                                />
                                <TextFieldElement
                                    name="notes"
                                    label={t("accounts.fields.notes")}
                                    multiline
                                    fullWidth
                                    sx={{ flex: 1 }}
                                    autoComplete="no-name"
                                    size="small"
                                />

                            </Stack>
                        )}

                        <RHFAutoCompleteString
                            name="tags"
                            label={t("accounts.fields.tags")}
                            size="small"
                            options={tags?.data ?? []}
                            multiple
                            createNew
                            sx={{ flex: 1 }}
                        />

                        {errors.root && (
                            <Alert severity="error">{errors.root.message}</Alert>
                        )}
                    </Stack>
                </Box>
            </CreateOrEdit>

            {/* Show user company details in edit mode */}
            {actionType === "edit" && (
                <Card
                    sx={{ mt: 3, p: 3 }}
                    cardHeaderProps={{
                        title: <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                            Company details filled by the user
                        </Typography>
                    }}
                >
                    <CardContent>
                        {formValues.getValues("user.company_details_form")
                            ? (<Stack spacing={2}>
                                <Stack direction="row" spacing={4}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Company Name</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.name || 'N/A'}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">City</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.city || 'N/A'}</Typography>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={4}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">PAN Number</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.pan_number || 'N/A'}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">GSTIN</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.gstin || 'N/A'}</Typography>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={4}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Email</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.email || 'N/A'}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Branch Name</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.branch_name || 'N/A'}</Typography>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={4}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">State</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.state || 'N/A'}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Pincode</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form")?.pincode || 'N/A'}</Typography>
                                    </Box>
                                </Stack>

                                {formValues.getValues("user.company_details_form")?.address && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Address</Typography>
                                        <Typography variant="body1">{formValues.getValues("user.company_details_form").address}</Typography>
                                    </Box>
                                )}

                                {(formValues.getValues("user.company_details_form")?.landline_number || formValues.getValues("user.company_details_form")?.transport_name) && (
                                    <Stack direction="row" spacing={4}>
                                        {formValues.getValues("user.company_details_form")?.landline_number && (
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">Landline Number</Typography>
                                                <Typography variant="body1">{formValues.getValues("user.company_details_form").landline_number}</Typography>
                                            </Box>
                                        )}
                                        {formValues.getValues("user.company_details_form")?.transport_name && (
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">Transport Name</Typography>
                                                <Typography variant="body1">{formValues.getValues("user.company_details_form").transport_name}</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                            </Stack>)
                            : (<Typography variant="body1">No company details found</Typography>)
                        }
                    </CardContent>
                </Card >
            )}

            <Modal
                open={confirmCompanyVisible}
                onClose={confirmCompanyClose}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 500,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                }}>
                    <Typography variant="h5">
                        Existing Company Found
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, mb: 3 }}>
                        Would you like to add this contact to the existing company <Typography component="span" variant="body1" fontWeight="bold">{formValues.getValues("existing-companies")?.[0]?.ledger_name}</Typography>?
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => {
                            confirmCompanyClose();
                            hasCheckedCompany.current = true;
                        }} variant="outlined">
                            No
                        </Button>
                        <Button onClick={handleConfirmExistingCompany} variant="contained" autoFocus>
                            Yes
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Modal
                open={newCompanyVisible}
                onClose={newCompanyClose}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 700,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '90vh',
                }}>
                    <Box onClick={(e) => e.stopPropagation()}>
                        <CompanyAddEditForm
                            actionType="create"
                            title="Add Company"
                            onSave={(ledgerName) => {
                                handleNewCompanySave(ledgerName);
                            }}
                            defaultFormValues={{
                                ledger_name: `${formValues.getValues("user.company_details_form")?.name.trim().toUpperCase()}, ${formValues.getValues("user.company_details_form")?.city.trim().toUpperCase()}`.toUpperCase(),
                                legal_name: formValues.getValues("user.company_details_form")?.name,
                                pan_number: formValues.getValues("user.company_details_form")?.pan_number,
                                gstin: formValues.getValues("user.company_details_form")?.gstin,
                                members: [formValues.getValues("id")],
                                category: "retailer",
                                type: "customer",
                                email: formValues.getValues("user.company_details_form")?.email,
                                addresses: [{
                                    branch_name: formValues.getValues("user.company_details_form")?.branch_name,
                                    city: formValues.getValues("user.company_details_form")?.city,
                                    country: formValues.getValues("user.company_details_form")?.country || "India",
                                    email: formValues.getValues("user.company_details_form")?.email,
                                    gstin: formValues.getValues("user.company_details_form")?.gstin,
                                    pincode: formValues.getValues("user.company_details_form")?.pincode,
                                    state: formValues.getValues("user.company_details_form")?.state,
                                    transport_name: formValues.getValues("user.company_details_form")?.transport_name,
                                    address: formValues.getValues("user.company_details_form")?.address,
                                    landline_number: formValues.getValues("user.company_details_form")?.landline_number,
                                }]
                            }}
                            redirect={false}
                        />
                    </Box>
                </Box>
            </Modal>
            <Modal
                open={newCompanyCreateVisible}
                onClose={newCompanyCreateClose}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 700,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '90vh',
                }}>
                    <CompanyAddEditForm
                        actionType="create"
                        redirect={false}
                        onSave={(ledgerName) => {
                            newCompanyCreateClose();
                            formValues.setValue("company_ledger_name", ledgerName);
                        }}
                    />
                </Box>
            </Modal>

        </>
    );
};
